import * as THREE from "three";
import { Engine } from "../../game/engine";
import { AudioBed } from "../../game/audio";
import {
  buildBikeMesh,
  clampLean,
  PALETTE_HELIOS,
  WHEEL_RADIUS,
} from "../_lib/moto";

/**
 * Throttle — vehicular / 2D moto-physics over procedural terrain.
 * Game 15 of 12 platform games + 6-game rebrand pack (MVP / v0).
 *
 * Design dossier: docs/dossier-games/15-throttle/
 * Source IP: Excite Bike (NES, 1984).
 *
 * MVP scope (per "first-cut" implementation note):
 *  - Procedural deterministic terrain from seed
 *  - Bike physics: throttle / lean / jump / gravity / friction
 *  - Terrain collision + landing detection (clean vs wreck)
 *  - 60s run cap; wreck or timeout ends run
 *  - Score = floor(distance_m) + 100 × clean_landings
 *
 * Deferred to v5 polish (NOT in MVP):
 *  - Music-reactive terrain pulse
 *  - Trick rotation combo multipliers (90/180/360/540°)
 *  - Session-best ghost trail
 *  - Engine RPM audio modulation
 *  - Tire-skid synth
 */

// ============================================================
// Constants — must stay byte-identical with scoring-throttle.ts
// (parity contract per dossier §02 §4.2)
// ============================================================

export const RUN_MS = 60_000;
export const SAMPLE_INTERVAL_MS = 100; // 10 Hz floor sample
export const MAX_INPUTS = 900;
export const MAX_LANDINGS = 60;
export const TERRAIN_LENGTH = 3000;
export const TERRAIN_GRID = 1.0; // one heightmap cell per world-x unit
export const TERRAIN_FLOOR = -2.0;
export const TERRAIN_AMP_END_SCALE = 1.8;
export const TRICK_BUCKET_DEG: readonly number[] = [90, 180, 360, 540, 720] as const;
export const TRICK_BUCKET_BONUS: readonly number[] = [50, 150, 400, 700, 1000] as const;
export const TRICK_COMBO_WINDOW_MS = 2500;
export const TRICK_COMBO_MAX = 3;

export const GRAVITY = -30.0;
export const THROTTLE_ACCEL = 18.0;
export const DRAG = 0.4;
export const MAX_FORWARD_SPEED = 36.0;
export const LEAN_TORQUE = 4.0;
export const MAX_LEAN_RATE = 2.0;
export const JUMP_IMPULSE = 12.0;

/** Landing buckets: chassis_angle in radians at moment of contact. */
export const LANDING_CLEAN_RAD = (5 * Math.PI) / 180; // ±5° = clean
export const LANDING_TILTED_RAD = (25 * Math.PI) / 180; // ±25° = tilted (penalty)
// Outside ±25° = wreck → run ends.

// ============================================================
// Terrain — deterministic heightmap from seed
// ============================================================

function makeRng(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Generate the terrain heightmap. Returns a Float32Array of heights
 * indexed by world-x (one entry per TERRAIN_GRID unit, so 3001 entries
 * for [0, TERRAIN_LENGTH]).
 *
 * The shape is a sum of seeded sine waves at different frequencies
 * plus a baseline ramp toward the ride's end (slight uphill encourages
 * speed buildup). Range: y ∈ [-2, +4] approximately.
 *
 * MUST match scoring-throttle.ts::generateTerrain byte-for-byte
 * (highest-risk parity contract per dossier §02 §4.2).
 */
export function generateTerrain(seed: number): Float32Array {
  const rng = makeRng(seed);
  const n = Math.floor(TERRAIN_LENGTH / TERRAIN_GRID) + 1;
  const heights = new Float32Array(n);

  // Three seeded sine waves
  const freqs = [
    { freq: 0.015 + rng() * 0.010, phase: rng() * Math.PI * 2, amp: 1.2 + rng() * 0.4 },
    { freq: 0.045 + rng() * 0.020, phase: rng() * Math.PI * 2, amp: 0.6 + rng() * 0.3 },
    { freq: 0.110 + rng() * 0.040, phase: rng() * Math.PI * 2, amp: 0.25 + rng() * 0.15 },
  ];

  const rampSpan = TERRAIN_LENGTH - 30;
  for (let i = 0; i < n; i++) {
    const x = i * TERRAIN_GRID;
    let h = 0;
    for (const s of freqs) {
      h += s.amp * Math.sin(s.freq * x + s.phase);
    }
    // Flat-start runway, then linear amplitude ramp from 1.0 to
    // TERRAIN_AMP_END_SCALE across the rest of the terrain (v2(b)
    // escalation). Same formula as the server validator.
    if (x < 30) {
      h = 0;
    } else {
      const t = Math.min(1, (x - 30) / Math.max(1, rampSpan));
      const scale = 1 + (TERRAIN_AMP_END_SCALE - 1) * t;
      h *= scale;
    }
    heights[i] = h;
  }
  return heights;
}

/** Sample the terrain at any continuous x via linear interpolation. */
export function terrainHeightAt(heights: Float32Array, x: number): number {
  if (x <= 0) return heights[0];
  if (x >= TERRAIN_LENGTH) return heights[heights.length - 1];
  const i = Math.floor(x / TERRAIN_GRID);
  const t = (x - i * TERRAIN_GRID) / TERRAIN_GRID;
  return heights[i] * (1 - t) + heights[i + 1] * t;
}

/** Build a THREE.Line that traces the terrain for rendering. */
function buildTerrainLine(heights: Float32Array): THREE.Line {
  const positions = new Float32Array(heights.length * 3);
  for (let i = 0; i < heights.length; i++) {
    positions[i * 3 + 0] = i * TERRAIN_GRID;
    positions[i * 3 + 1] = heights[i];
    positions[i * 3 + 2] = 0;
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  const mat = new THREE.LineBasicMaterial({
    color: 0x66ddff,
    transparent: true,
    opacity: 0.9,
  });
  return new THREE.Line(geo, mat);
}

/** Build a filled terrain mesh for the area below the line (ground). */
function buildTerrainFill(heights: Float32Array): THREE.Mesh {
  const segCount = heights.length - 1;
  const triCount = segCount * 2;
  const positions = new Float32Array(triCount * 3 * 3);
  let p = 0;
  for (let i = 0; i < segCount; i++) {
    const x0 = i * TERRAIN_GRID;
    const x1 = (i + 1) * TERRAIN_GRID;
    const y0 = heights[i];
    const y1 = heights[i + 1];
    // Triangle 1: (x0, y0), (x1, y1), (x0, FLOOR)
    positions[p++] = x0; positions[p++] = y0; positions[p++] = -0.1;
    positions[p++] = x1; positions[p++] = y1; positions[p++] = -0.1;
    positions[p++] = x0; positions[p++] = TERRAIN_FLOOR; positions[p++] = -0.1;
    // Triangle 2: (x1, y1), (x1, FLOOR), (x0, FLOOR)
    positions[p++] = x1; positions[p++] = y1; positions[p++] = -0.1;
    positions[p++] = x1; positions[p++] = TERRAIN_FLOOR; positions[p++] = -0.1;
    positions[p++] = x0; positions[p++] = TERRAIN_FLOOR; positions[p++] = -0.1;
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geo.computeVertexNormals();
  const mat = new THREE.MeshStandardMaterial({
    color: 0x081020,
    roughness: 0.9,
    metalness: 0.1,
  });
  return new THREE.Mesh(geo, mat);
}

// ============================================================
// Input + Events
// ============================================================

export interface InputSample {
  t: number; // ms since run start
  throttle: 0 | 1;
  lean: number; // -1..1, quantized 0.01
  jump: 0 | 1;
}

export interface LandingEvent {
  t: number;
  x: number; // quantized 0.01
  y: number; // quantized 0.01
  rotation_deg: number; // quantized 1°
  bucket: "clean" | "tilted";
}

export interface WreckEvent {
  t: number;
  x: number;
  rotation_deg: number;
}

export interface ThrottleResult {
  distance_m: number;
  clean_landings: number;
  tilted_landings: number;
  wrecked: boolean;
  duration_ms: number;
  inputs: InputSample[];
  landings: LandingEvent[];
  wreck: WreckEvent | null;
  scoreEstimate: number;
  /** v5: trick scoring */
  trickBonus: number;
  trickCount: number;
  peakTrickCombo: number;
}

export interface ThrottleCallbacks {
  onHud: (runMs: number, distance: number, cleanLandings: number, scoreEstimate: number) => void;
  onGameOver: (result: ThrottleResult) => void;
}

// ============================================================
// Game class
// ============================================================

export class Throttle {
  private engine: Engine;
  private audio = new AudioBed();
  private heights: Float32Array;
  private bikeGroup: THREE.Group;
  private terrainLine!: THREE.Line;
  private terrainFill!: THREE.Mesh;

  // v6 — parallax starfield layers placed behind the terrain in -z so
  // the side-scrolling action reads against real depth, not a flat
  // background-color wall. Per memory feedback-sibling-games-shallow.
  private starLayers: THREE.Points[] = [];
  private envLight: THREE.PointLight;

  // Bike state
  private bx = 0;
  private by = 0;
  private bvx = 0;
  private bvy = 0;
  // Boost intensity proxy — drives Engine.setBoost for FOV pulse +
  // motion blur + chromatic aberration (depth-bar post-processing).
  // Maps current forward speed ratio to 0..1.
  private getBoostLevel(): number {
    return Math.max(0, Math.min(1, this.bvx / MAX_FORWARD_SPEED));
  }
  private chassisAngle = 0; // radians
  private chassisAngVel = 0;
  private onGround = true;
  private justLanded = false;

  // Input state
  private inThrottle = false;
  private inLeanRaw = 0;
  private inLeanSmooth = 0;
  private wantJump = false;

  // Recordings
  private startedAt = 0;
  private inputs: InputSample[] = [];
  private landings: LandingEvent[] = [];
  private wreckEvent: WreckEvent | null = null;
  private samplerTimer: number = 0;
  private lastInputSnapshot: InputSample = { t: 0, throttle: 0, lean: 0, jump: 0 };

  // Counts
  private cleanLandings = 0;
  private tiltedLandings = 0;
  private gameOver = false;
  private cb: ThrottleCallbacks;

  // v5 trick state
  private airAngleAccum = 0;
  private trickBonus = 0;
  private trickCombo = 0;
  private peakTrickCombo = 0;
  private trickCount = 0;
  private lastTrickLandingT = -Infinity;

  // Phase 2: PB ghost. Last-saved run's input stream is loaded and
  // replayed in parallel with the current run — a translucent bike
  // shows the player what their best historical attempt was doing at
  // this run-time. Pure visual; no scoring impact.
  private ghostInputs: InputSample[] | null = null;
  private ghostMesh: THREE.Group | null = null;
  private ghostState = {
    bx: 0, by: 0, bvx: 0, bvy: 0,
    chassisAngle: 0, chassisAngVel: 0,
    onGround: true, leanSmooth: 0,
    lastJumpT: -1, alive: true,
  };
  /** Inject PB inputs (called from main-throttle.ts on construct). */
  setGhostInputs(inputs: InputSample[]) {
    if (!inputs || inputs.length === 0) return;
    this.ghostInputs = inputs;
  }
  private trickBucketFor(airRotationDeg: number): number {
    const abs = Math.abs(airRotationDeg);
    let bonus = 0;
    for (let i = 0; i < TRICK_BUCKET_DEG.length; i++) {
      if (abs >= TRICK_BUCKET_DEG[i]) bonus = TRICK_BUCKET_BONUS[i];
      else break;
    }
    return bonus;
  }

  private onKeyDown = (e: KeyboardEvent) => {
    if (e.code === "ArrowUp" || e.code === "KeyW") { this.inThrottle = true; this.recordEdge(); }
    if (e.code === "ArrowLeft" || e.code === "KeyA") { this.inLeanRaw = -1; this.recordEdge(); }
    if (e.code === "ArrowRight" || e.code === "KeyD") { this.inLeanRaw = 1; this.recordEdge(); }
    if (e.code === "Space") { e.preventDefault(); if (this.onGround) { this.wantJump = true; this.recordEdge(); } }
  };
  private onKeyUp = (e: KeyboardEvent) => {
    if (e.code === "ArrowUp" || e.code === "KeyW") { this.inThrottle = false; this.recordEdge(); }
    if (e.code === "ArrowLeft" || e.code === "KeyA") { if (this.inLeanRaw === -1) this.inLeanRaw = 0; this.recordEdge(); }
    if (e.code === "ArrowRight" || e.code === "KeyD") { if (this.inLeanRaw === 1) this.inLeanRaw = 0; this.recordEdge(); }
  };

  // Mobile touch handlers. Lean strip (left half) sets inLeanRaw to
  // a continuous [-1, 1] from the touch X within the strip. GO + JUMP
  // chips flip the same flags the keyboard does. recordEdge fires on
  // every transition so the recorded inputs[] stream stays well-sampled.
  private touchLeanEl: HTMLElement | null = null;
  private touchGoEl: HTMLElement | null = null;
  private touchJumpEl: HTMLElement | null = null;
  private leanTouchId: number | null = null;
  private updateLeanFromPointer = (e: PointerEvent) => {
    if (!this.touchLeanEl) return;
    const r = this.touchLeanEl.getBoundingClientRect();
    const frac = (e.clientX - r.left) / Math.max(1, r.width);  // 0..1
    this.inLeanRaw = Math.max(-1, Math.min(1, frac * 2 - 1));
    this.recordEdge();
  };
  private onTouchLeanDown = (e: PointerEvent) => {
    e.preventDefault();
    this.leanTouchId = e.pointerId;
    if (this.touchLeanEl) this.touchLeanEl.setPointerCapture(e.pointerId);
    this.updateLeanFromPointer(e);
  };
  private onTouchLeanMove = (e: PointerEvent) => {
    if (this.leanTouchId !== e.pointerId) return;
    e.preventDefault();
    this.updateLeanFromPointer(e);
  };
  private onTouchLeanUp = (e: PointerEvent) => {
    if (this.leanTouchId !== e.pointerId) return;
    if (this.touchLeanEl) {
      try { this.touchLeanEl.releasePointerCapture(e.pointerId); } catch { /* not captured */ }
    }
    this.leanTouchId = null;
    this.inLeanRaw = 0;
    this.recordEdge();
  };
  private onTouchGoDown = (e: PointerEvent) => { e.preventDefault(); this.inThrottle = true; this.recordEdge(); };
  private onTouchGoUp = (e: PointerEvent) => { e.preventDefault(); this.inThrottle = false; this.recordEdge(); };
  private onTouchJumpDown = (e: PointerEvent) => {
    e.preventDefault();
    if (this.onGround) { this.wantJump = true; this.recordEdge(); }
  };
  private mountTouch() {
    this.touchLeanEl = document.getElementById("throttle-touch-lean");
    this.touchGoEl = document.getElementById("throttle-touch-go");
    this.touchJumpEl = document.getElementById("throttle-touch-jump");
    if (this.touchLeanEl) {
      this.touchLeanEl.addEventListener("pointerdown", this.onTouchLeanDown);
      this.touchLeanEl.addEventListener("pointermove", this.onTouchLeanMove);
      this.touchLeanEl.addEventListener("pointerup", this.onTouchLeanUp);
      this.touchLeanEl.addEventListener("pointercancel", this.onTouchLeanUp);
    }
    if (this.touchGoEl) {
      this.touchGoEl.addEventListener("pointerdown", this.onTouchGoDown);
      this.touchGoEl.addEventListener("pointerup", this.onTouchGoUp);
      this.touchGoEl.addEventListener("pointercancel", this.onTouchGoUp);
    }
    if (this.touchJumpEl) this.touchJumpEl.addEventListener("pointerdown", this.onTouchJumpDown);
  }
  private unmountTouch() {
    if (this.touchLeanEl) {
      this.touchLeanEl.removeEventListener("pointerdown", this.onTouchLeanDown);
      this.touchLeanEl.removeEventListener("pointermove", this.onTouchLeanMove);
      this.touchLeanEl.removeEventListener("pointerup", this.onTouchLeanUp);
      this.touchLeanEl.removeEventListener("pointercancel", this.onTouchLeanUp);
    }
    if (this.touchGoEl) {
      this.touchGoEl.removeEventListener("pointerdown", this.onTouchGoDown);
      this.touchGoEl.removeEventListener("pointerup", this.onTouchGoUp);
      this.touchGoEl.removeEventListener("pointercancel", this.onTouchGoUp);
    }
    if (this.touchJumpEl) this.touchJumpEl.removeEventListener("pointerdown", this.onTouchJumpDown);
  }

  constructor(canvas: HTMLCanvasElement, cb: ThrottleCallbacks, seed: number) {
    this.cb = cb;
    this.heights = generateTerrain(seed);
    this.bikeGroup = buildBikeMesh(PALETTE_HELIOS);

    this.engine = new Engine(canvas, { onFrame: (dt) => this.frame(dt) });
    // Side-view perspective camera
    this.engine.camera.position.set(0, 2, 14);
    this.engine.camera.lookAt(0, 1, 0);
    this.engine.camera.fov = 56;
    this.engine.camera.updateProjectionMatrix();
    this.engine.scene.fog = new THREE.Fog(0x040814, 18, 80);
    this.engine.scene.background = new THREE.Color(0x040814);

    // Lighting
    this.engine.scene.add(new THREE.AmbientLight(0x506080, 0.45));
    const key = new THREE.DirectionalLight(0xfff0d0, 0.9);
    key.position.set(8, 12, 8);
    this.engine.scene.add(key);
    this.envLight = new THREE.PointLight(0xff8a40, 1.2, 22, 1.5);
    this.envLight.position.set(0, 4, 4);
    this.engine.scene.add(this.envLight);

    // Terrain
    this.terrainLine = buildTerrainLine(this.heights);
    this.terrainFill = buildTerrainFill(this.heights);
    this.engine.scene.add(this.terrainLine);
    this.engine.scene.add(this.terrainFill);

    // v6 — parallax starfield. Three layers at varying -Z give the
    // night-ride canon real depth instead of a flat color wall behind
    // the terrain. Per memory feedback-sibling-games-shallow. Seed-
    // derived positions so the same starscape recurs per-run.
    const makeStars = (count: number, z: number, spread: { x: number; y: number }, opts: { size: number; color: number }) => {
      const positions = new Float32Array(count * 3);
      let s = (seed ^ Math.imul(z | 0, 0x9e3779b1)) | 0;
      const r = () => {
        s = (s + 0x6d2b79f5) | 0;
        let t = s;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
      };
      for (let i = 0; i < count; i++) {
        positions[i * 3] = (r() - 0.5) * spread.x * 2;
        // Bias upward — most stars sit in the sky half of the frame so
        // they don't bleed through the terrain band.
        positions[i * 3 + 1] = (r() - 0.2) * spread.y;
        positions[i * 3 + 2] = z + (r() - 0.5) * 20;
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      const mat = new THREE.PointsMaterial({
        color: opts.color, size: opts.size, sizeAttenuation: true,
        transparent: true, opacity: 0.85, depthWrite: false, fog: false,
      });
      const pts = new THREE.Points(geo, mat);
      this.engine.scene.add(pts);
      this.starLayers.push(pts);
    };
    makeStars(450, -220, { x: 220, y: 70 }, { size: 0.9, color: 0x8a92b8 });   // far cool
    makeStars(220, -120, { x: 130, y: 50 }, { size: 0.75, color: 0xb0b8d8 });  // mid
    makeStars(90,   -55, { x: 80,  y: 40 }, { size: 0.6,  color: 0xffe0c0 });  // near warm

    // Bike
    this.engine.scene.add(this.bikeGroup);

    // Initialize bike at the runway start
    this.bx = 0;
    this.by = terrainHeightAt(this.heights, 0) + WHEEL_RADIUS;
    this.bvx = 0;
    this.bvy = 0;
    this.chassisAngle = 0;
    this.bikeGroup.position.set(this.bx, this.by, 0);

    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    this.mountTouch();
    this.buildGhostMesh();
  }

  /** Phase 2: build a translucent ghost bike for PB replay. Visible
   *  only if ghostInputs has been set (otherwise the mesh stays hidden). */
  private buildGhostMesh() {
    this.ghostMesh = buildBikeMesh({
      chassis: 0x6680ff, emissive: 0x4060c0, wheels: 0x222244, rider: 0xccddff,
    });
    this.ghostMesh.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        // Clone material so we can make it translucent without affecting
        // the player bike (which shares the palette helper's material).
        const orig = obj.material as THREE.MeshStandardMaterial;
        const ghostMat = orig.clone();
        ghostMat.transparent = true;
        ghostMat.opacity = 0.42;
        ghostMat.depthWrite = false;
        obj.material = ghostMat;
      }
    });
    this.ghostMesh.visible = false;
    this.engine.scene.add(this.ghostMesh);
  }

  /** Phase 2: advance ghost state one fixed-dt step using its input
   *  stream, mirroring the same physics the main bike runs. Ghost
   *  doesn't affect terrain or scoring — purely visual. */
  private stepGhost(runMs: number, dt: number) {
    if (!this.ghostInputs || !this.ghostState.alive || !this.ghostMesh) return;
    // Pick input at runMs.
    const inputs = this.ghostInputs;
    let idx = 0;
    for (let i = 0; i < inputs.length; i++) {
      if (inputs[i].t <= runMs) idx = i; else break;
    }
    const cur = inputs[idx];
    if (runMs > inputs[inputs.length - 1].t + 200) {
      // Ghost run ended; freeze + fade.
      this.ghostState.alive = false;
      return;
    }
    const g = this.ghostState;
    const inThrottle = cur.throttle === 1;
    const inLeanRaw = cur.lean;
    const wantJump = cur.jump === 1 && cur.t !== g.lastJumpT;

    g.leanSmooth = clampLean(g.leanSmooth, inLeanRaw, dt, MAX_LEAN_RATE);

    if (inThrottle && g.onGround) g.bvx += THROTTLE_ACCEL * dt;
    g.bvx *= 1 - DRAG * dt;
    if (g.bvx > MAX_FORWARD_SPEED) g.bvx = MAX_FORWARD_SPEED;
    if (g.bvx < 0) g.bvx = 0;

    if (wantJump && g.onGround) {
      g.bvy = JUMP_IMPULSE;
      g.onGround = false;
      g.lastJumpT = cur.t;
    }
    g.bvy += GRAVITY * dt;

    if (!g.onGround) {
      g.chassisAngVel += LEAN_TORQUE * g.leanSmooth * dt;
      g.chassisAngVel = Math.max(-3.0, Math.min(3.0, g.chassisAngVel));
    } else {
      g.chassisAngVel *= 1 - 6 * dt;
    }
    g.chassisAngle += g.chassisAngVel * dt;

    g.bx += g.bvx * dt;
    g.by += g.bvy * dt;

    const terrainY = terrainHeightAt(this.heights, g.bx);
    const wheelBottom = g.by - WHEEL_RADIUS;
    if (wheelBottom <= terrainY) {
      g.by = terrainY + WHEEL_RADIUS;
      if (g.bvy < 0) g.bvy = 0;
      if (!g.onGround) {
        // Ghost ignores wreck-vs-clean distinction; just lands.
        g.onGround = true;
        g.chassisAngle *= 0.92;
      }
    } else {
      g.onGround = false;
    }

    // Push to mesh.
    this.ghostMesh.position.set(g.bx, g.by, 0.4);  // slightly forward in z so it doesn't z-fight
    this.ghostMesh.rotation.z = g.chassisAngle;
    this.ghostMesh.visible = true;
  }

  private recordEdge() {
    if (this.gameOver) return;
    const t = performance.now() - this.startedAt;
    if (t < 0) return;
    const snap: InputSample = {
      t: Math.round(t),
      throttle: this.inThrottle ? 1 : 0,
      lean: Math.round(this.inLeanRaw * 100) / 100,
      jump: this.wantJump ? 1 : 0,
    };
    if (
      this.inputs.length === 0 ||
      snap.throttle !== this.lastInputSnapshot.throttle ||
      snap.lean !== this.lastInputSnapshot.lean ||
      snap.jump !== this.lastInputSnapshot.jump
    ) {
      this.inputs.push(snap);
      this.lastInputSnapshot = snap;
    }
  }

  private takeFloorSample() {
    if (this.gameOver) return;
    const t = performance.now() - this.startedAt;
    if (t < 0 || t > RUN_MS) return;
    const snap: InputSample = {
      t: Math.round(t),
      throttle: this.inThrottle ? 1 : 0,
      lean: Math.round(this.inLeanRaw * 100) / 100,
      jump: 0,
    };
    this.inputs.push(snap);
    this.lastInputSnapshot = snap;
  }

  start() {
    this.audio.arm();
    // v6 — seed drone at baseline. Per-frame updates in frame() ramp
    // it with current speed (replacing the fixed 0.55 baseline). Per
    // memory feedback-sibling-games-shallow §"escalation hook".
    this.audio.setDroneIntensity(0.15);
    this.startedAt = performance.now();
    this.gameOver = false;
    this.inputs = [];
    this.landings = [];
    this.wreckEvent = null;
    this.cleanLandings = 0;
    this.tiltedLandings = 0;
    // Seed an initial input snapshot at t=0
    this.inputs.push({ t: 0, throttle: 0, lean: 0, jump: 0 });
    this.lastInputSnapshot = this.inputs[0];
    if (this.samplerTimer) clearInterval(this.samplerTimer);
    this.samplerTimer = window.setInterval(() => this.takeFloorSample(), SAMPLE_INTERVAL_MS);
    this.engine.start();
  }

  private frame(dt: number) {
    if (this.gameOver) return;
    const now = performance.now() - this.startedAt;
    if (now >= RUN_MS) {
      this.finish(false);
      return;
    }
    const boost = this.getBoostLevel();
    this.engine.setBoost(boost, dt);
    // v6 — adaptive drone tracks the same speed-derived boost level.
    // Capped at 0.7. AudioBed.setDroneIntensity damps internally so
    // per-frame setting is cheap.
    try { this.audio.setDroneIntensity(0.15 + boost * 0.55); } catch { /* off */ }
    this.stepGhost(now, dt);

    // Smooth lean
    this.inLeanSmooth = clampLean(this.inLeanSmooth, this.inLeanRaw, dt, MAX_LEAN_RATE);

    // Throttle force (only on ground)
    if (this.inThrottle && this.onGround) {
      this.bvx += THROTTLE_ACCEL * dt;
    }
    // Drag
    this.bvx *= 1 - DRAG * dt;
    if (this.bvx > MAX_FORWARD_SPEED) this.bvx = MAX_FORWARD_SPEED;
    if (this.bvx < 0) this.bvx = 0;

    // Jump (instant impulse)
    if (this.wantJump && this.onGround) {
      this.bvy = JUMP_IMPULSE;
      this.onGround = false;
    }
    this.wantJump = false;

    // Gravity
    this.bvy += GRAVITY * dt;

    // Lean torque (only in air; ground keeps chassis aligned to terrain)
    if (!this.onGround) {
      this.chassisAngVel += LEAN_TORQUE * this.inLeanSmooth * dt;
      this.chassisAngVel = Math.max(-3.0, Math.min(3.0, this.chassisAngVel));
      // v5: accumulate in-air rotation for trick scoring.
      this.airAngleAccum += this.chassisAngVel * dt;
    } else {
      // On-ground: angle settles toward terrain slope; angular vel damped.
      this.chassisAngVel *= 1 - 6 * dt;
    }
    this.chassisAngle += this.chassisAngVel * dt;

    // Integrate position
    this.bx += this.bvx * dt;
    this.by += this.bvy * dt;

    // Terrain collision (front wheel sample)
    const terrainY = terrainHeightAt(this.heights, this.bx);
    const wheelBottom = this.by - WHEEL_RADIUS;
    const wasOnGround = this.onGround;
    if (wheelBottom <= terrainY) {
      // Land
      this.by = terrainY + WHEEL_RADIUS;
      if (this.bvy < 0) this.bvy = 0;
      if (!this.onGround) {
        this.justLanded = true;
        this.handleLanding(now);
      }
      this.onGround = true;
      // Settle chassis toward 0 (terrain-aligned, approximated)
      this.chassisAngle *= 0.92;
    } else {
      this.onGround = false;
      this.justLanded = false;
    }
    // v5: reset air-rotation accumulator on every takeoff transition.
    if (wasOnGround && !this.onGround) this.airAngleAccum = 0;

    // Update bike mesh
    this.bikeGroup.position.set(this.bx, this.by, 0);
    this.bikeGroup.rotation.z = this.chassisAngle;

    // Camera follow
    this.engine.camera.position.x = this.bx - 5; // a little behind so bike is in screen-right
    this.engine.camera.position.y = Math.max(2, this.by + 1.5);
    this.engine.camera.lookAt(this.bx + 4, this.by + 0.5, 0);

    // Env light follows bike
    this.envLight.position.set(this.bx + 2, this.by + 3, 4);

    // HUD
    const scoreEstimate = Math.floor(this.bx) + this.cleanLandings * 100 + this.trickBonus;
    this.cb.onHud(now, this.bx, this.cleanLandings, scoreEstimate);

    // End if past terrain or wrecked
    if (this.bx >= TERRAIN_LENGTH) {
      this.finish(false);
    }
  }

  private handleLanding(now: number) {
    const angleAbs = Math.abs(this.chassisAngle);
    const x = Math.round(this.bx * 100) / 100;
    const y = Math.round(this.by * 100) / 100;
    const rotationDeg = Math.round((this.chassisAngle * 180) / Math.PI);

    if (angleAbs <= LANDING_CLEAN_RAD) {
      this.cleanLandings += 1;
      const landingT = Math.round(now);
      this.landings.push({ t: landingT, x, y, rotation_deg: rotationDeg, bucket: "clean" });
      // v5: trick award.
      const bonus = this.trickBucketFor((this.airAngleAccum * 180) / Math.PI);
      if (bonus > 0) {
        if (landingT - this.lastTrickLandingT <= TRICK_COMBO_WINDOW_MS) {
          this.trickCombo = Math.min(TRICK_COMBO_MAX, this.trickCombo + 1);
        } else {
          this.trickCombo = 1;
        }
        this.trickBonus += bonus * this.trickCombo;
        if (this.trickCombo > this.peakTrickCombo) this.peakTrickCombo = this.trickCombo;
        this.trickCount += 1;
        this.lastTrickLandingT = landingT;
        try { this.audio.chime(60 + 30 * this.trickCombo, 0); } catch { /* off */ }
      } else {
        this.audio.chime(12, 0);
      }
    } else if (angleAbs <= LANDING_TILTED_RAD) {
      this.tiltedLandings += 1;
      this.landings.push({ t: Math.round(now), x, y, rotation_deg: rotationDeg, bucket: "tilted" });
      this.audio.chime(7, 0);
    } else {
      // WRECK
      this.wreckEvent = { t: Math.round(now), x, rotation_deg: rotationDeg };
      this.audio.thud();
      this.finish(true);
    }
  }

  private finish(wrecked: boolean) {
    if (this.gameOver) return;
    this.gameOver = true;
    if (this.samplerTimer) {
      clearInterval(this.samplerTimer);
      this.samplerTimer = 0;
    }
    this.audio.setDroneIntensity(0);

    const distance = Math.round(this.bx * 100) / 100;
    const scoreEstimate = Math.floor(distance) + this.cleanLandings * 100 + this.trickBonus;

    this.cb.onGameOver({
      distance_m: distance,
      clean_landings: this.cleanLandings,
      tilted_landings: this.tiltedLandings,
      wrecked,
      duration_ms: Math.round(performance.now() - this.startedAt),
      inputs: this.inputs,
      landings: this.landings,
      wreck: this.wreckEvent,
      scoreEstimate,
      trickBonus: this.trickBonus,
      trickCount: this.trickCount,
      peakTrickCombo: this.peakTrickCombo,
    });
  }

  setMuted(b: boolean) { this.audio.setMuted(b); }
  setVolume(v: number) { this.audio.setVolume(v); }
  armAudio() { this.audio.arm(); }

  dispose() {
    try { this.audio.setDroneIntensity(0); } catch { /* off */ }
    // v6 — drop the parallax starfield layers so a navigate-away →
    // remount cycle doesn't accumulate THREE.Points.
    for (const layer of this.starLayers) {
      this.engine.scene.remove(layer);
      layer.geometry.dispose();
      (layer.material as THREE.Material).dispose();
    }
    this.starLayers = [];
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    this.unmountTouch();
    if (this.samplerTimer) clearInterval(this.samplerTimer);
    this.engine.dispose();
  }
}
